import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Upload, X, Check, Camera, AlertCircle, LogOut, User } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function CompleteRegistrationPage() {
  const navigate = useNavigate();
  const { user, api, refreshUser, logout } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [existingPhotos, setExistingPhotos] = useState(0);
  const [profilePhotoIndex, setProfilePhotoIndex] = useState(0); // Index of selected profile photo

  useEffect(() => {
    // If user is admin or already has profile_complete, redirect to feed
    if (user?.is_admin || user?.profile_complete) {
      navigate('/feed');
      return;
    }
    
    // Check if user already has photos
    const checkPhotos = async () => {
      try {
        const response = await api.get('/profile');
        const photoCount = response.data.photos?.length || 0;
        setExistingPhotos(photoCount);
        
        if (photoCount >= 2) {
          toast.success('Inscription complète !');
          navigate('/feed');
        }
      } catch (error) {
        // Profile might not exist yet
      }
    };
    checkPhotos();
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    const newPhotos = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setPhotos([...photos, ...newPhotos]);
  };

  const removePhoto = (index) => {
    const newPhotos = [...photos];
    URL.revokeObjectURL(newPhotos[index].preview);
    newPhotos.splice(index, 1);
    setPhotos(newPhotos);
    
    // Adjust profile photo index if needed
    if (profilePhotoIndex >= newPhotos.length && newPhotos.length > 0) {
      setProfilePhotoIndex(0);
    } else if (profilePhotoIndex > index) {
      setProfilePhotoIndex(profilePhotoIndex - 1);
    }
  };

  const selectProfilePhoto = (index) => {
    setProfilePhotoIndex(index);
    toast.success('Photo de profil sélectionnée');
  };

  const handleSubmit = async () => {
    const totalPhotos = existingPhotos + photos.length;
    if (totalPhotos < 2) {
      toast.error(`Vous devez ajouter au minimum ${2 - existingPhotos} photo(s)`);
      return;
    }

    setUploading(true);

    try {
      // Upload all photos, marking the selected one as profile picture
      for (let i = 0; i < photos.length; i++) {
        const formData = new FormData();
        formData.append('file', photos[i].file);
        formData.append('is_private', 'false');
        
        // The selected photo becomes profile picture (only if no existing photos)
        if (i === profilePhotoIndex && existingPhotos === 0) {
          formData.append('is_profile_picture', 'true');
        }
        
        await api.post('/photos', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      await refreshUser();
      toast.success('Inscription complète ! Votre profil est en attente de validation par la communauté.');
      navigate('/feed');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'upload des photos';
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const photosNeeded = Math.max(0, 2 - existingPhotos - photos.length);

  return (
    <div className="min-h-screen bg-dark py-12 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Logout button at top */}
        <div className="flex justify-end mb-4">
          <Button 
            onClick={handleLogout}
            variant="outline"
            className="text-red-400 border-red-400/50 hover:bg-red-400/10"
          >
            <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
          </Button>
        </div>
        
        <div className="glass-card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Camera className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-serif text-3xl gold-gradient-text mb-2">
              Complétez votre inscription
            </h1>
            <p className="text-gray-400">
              Ajoutez au minimum 2 photos pour que votre profil soit soumis à la validation de la communauté.
            </p>
          </div>

          {/* Warning */}
          <div className="p-4 bg-gold/10 border border-gold/30 rounded mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-gold mt-0.5" />
              <div className="text-sm">
                <p className="text-gold font-semibold mb-1">Conseils</p>
                <p className="text-gray-400 italic">
                  Nous vous conseillons de proposer des photos de vous élégantes... et suggestives si vous voulez rester discret...
                </p>
              </div>
            </div>
          </div>

          {/* Profile photo selection hint */}
          {photos.length > 1 && existingPhotos === 0 && (
            <div className="p-3 bg-dark-subtle border border-white/10 rounded mb-4">
              <div className="flex items-center gap-2 text-sm text-gray-300">
                <User className="w-4 h-4 text-gold" />
                <span>Cliquez sur une photo pour la définir comme <span className="text-gold font-semibold">photo de profil</span></span>
              </div>
            </div>
          )}

          {/* Existing photos indicator */}
          {existingPhotos > 0 && (
            <div className="mb-4 text-sm text-gray-400">
              Vous avez déjà {existingPhotos} photo(s). Il vous en faut encore {Math.max(0, 2 - existingPhotos)}.
            </div>
          )}

          {/* Photo upload area */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {/* Upload button */}
            <label className="aspect-square border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-gold/50 transition-colors">
              <Upload className="w-8 h-8 text-gray-500 mb-2" />
              <span className="text-sm text-gray-500">Ajouter</span>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoUpload}
                className="hidden"
                disabled={uploading}
              />
            </label>

            {/* Photo previews */}
            {photos.map((photo, index) => (
              <div 
                key={index} 
                className={`aspect-square relative rounded-lg overflow-hidden cursor-pointer transition-all ${
                  index === profilePhotoIndex && existingPhotos === 0
                    ? 'ring-2 ring-gold ring-offset-2 ring-offset-dark'
                    : 'hover:ring-2 hover:ring-white/30 hover:ring-offset-2 hover:ring-offset-dark'
                }`}
                onClick={() => existingPhotos === 0 && selectProfilePhoto(index)}
                data-testid={`photo-${index}`}
              >
                <img
                  src={photo.preview}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                
                {/* Remove button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removePhoto(index);
                  }}
                  className="absolute top-2 right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors z-10"
                  data-testid={`remove-photo-${index}`}
                >
                  <X className="w-4 h-4 text-white" />
                </button>
                
                {/* Profile photo badge */}
                {index === profilePhotoIndex && existingPhotos === 0 && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <span className="flex items-center justify-center gap-1 text-gold text-xs font-semibold">
                      <User className="w-3 h-3" />
                      Photo de profil
                    </span>
                  </div>
                )}
                
                {/* Clickable hint overlay for non-selected photos */}
                {index !== profilePhotoIndex && existingPhotos === 0 && photos.length > 1 && (
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                    <span className="text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
                      Définir comme profil
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Status */}
          <div className="flex items-center justify-between mb-6 p-4 bg-dark-subtle rounded">
            <span className="text-gray-400">Photos sélectionnées</span>
            <span className={`font-semibold ${existingPhotos + photos.length >= 2 ? 'text-green-400' : 'text-gold'}`}>
              {existingPhotos + photos.length} / 2 minimum
            </span>
          </div>

          {/* Submit button */}
          <Button
            onClick={handleSubmit}
            disabled={uploading || photosNeeded > 0}
            className="w-full btn-gold py-6 text-lg"
            data-testid="complete-registration-btn"
          >
            {uploading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-dark/30 border-t-dark rounded-full animate-spin" />
                Upload en cours...
              </div>
            ) : photosNeeded > 0 ? (
              `Ajoutez encore ${photosNeeded} photo(s)`
            ) : (
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5" />
                Terminer l'inscription
              </div>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
